"use client";


import { Box, LoadingOverlay, rem, useMantineColorScheme } from "@mantine/core";
import type {
    Dashboard,
    DashboardItem,
    GridPosition,
} from "@bi-studio/shared/lib";
import { DashboardViewLayout } from "./DashboardViewLayout";

export interface DashboardViewProps {
    isRendering: boolean;
    renderedItems: any[];
    dashboard: Dashboard & { items?: DashboardItem[] };
    chartLayouts: Map<string, GridPosition>;
    isFullscreen: boolean;
    token: string;
    onItemPageChange: (itemId: string, page: number) => Promise<void>;
}



export function DashboardView({
    isRendering,
    renderedItems,
    dashboard,
    chartLayouts,
    isFullscreen,
    token,
    onItemPageChange,
}: DashboardViewProps) {
    const { colorScheme } = useMantineColorScheme();

    return (
        <div className={isFullscreen ? "flex-1 overflow-y-auto" : "space-y-4"}>
            <div className={isFullscreen ? "" : "space-y-4"}>
                {/* Rendered Charts - Prominently displayed */}
                {isRendering ? (
                    <div className="rounded-md border"
                        style={{
                            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : undefined,
                            borderColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : undefined,
                            minHeight: rem(400),
                            position: 'relative'
                        }}>
                        <LoadingOverlay
                            visible={isRendering}
                            overlayProps={{ radius: "sm", opacity: 0 }}
                        />
                    </div>
                ) : renderedItems.length > 0 ? (
                    <Box
                        style={{
                            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'var(--mantine-color-white)',
                            borderRadius: 'var(--mantine-radius-md)',
                            border: '1px solid var(--modal-border)',
                            backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined,
                            boxShadow: 'var(--mantine-shadow-sm)',
                            position: 'relative',
                            padding: isFullscreen ? rem(16) : rem(16)
                        }}
                    >
                        <DashboardViewLayout
                            key={isFullscreen ? "fullscreen" : "view"}
                            items={renderedItems.map((item) => ({
                                id: item.id,
                                chart: item.chart,
                                report: item.report,
                                data: item.data,
                                layout: item.chart
                                    ? chartLayouts.get(item.chart.id)
                                    : item.report
                                        ? chartLayouts.get(item.report.id)
                                        : undefined,
                            }))}
                            token={token || ""}
                            onItemPageChange={onItemPageChange}
                        />
                    </Box>
                ) : dashboard.items && dashboard.items.length > 0 ? (
                    <div className="dashboard-grid space-y-4">
                        <Box
                            p="sm"
                            style={{
                                borderRadius: 'var(--mantine-radius-md)',
                                border: '1px solid var(--mantine-color-yellow-outline)',
                                backgroundColor: 'rgba(242, 185, 41, 0.1)',
                                color: colorScheme === 'dark' ? 'var(--mantine-color-yellow-2)' : 'var(--mantine-color-yellow-9)',
                                fontSize: 'var(--mantine-font-size-sm)'
                            }}
                        >
                            {renderedItems.length === 0
                                ? "Items are configured but could not be rendered. This might be due to missing data or configuration issues."
                                : `Only ${renderedItems.length} of ${dashboard.items.length} item${dashboard.items.length !== 1 ? "s" : ""} could be rendered.`}
                        </Box>
                    </div>
                ) : (
                    <Box
                        py="xl"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--mantine-color-dimmed)'
                        }}
                    >
                        No items in this dashboard. Add charts and reports using the "View Details" button.
                    </Box>
                )}
            </div>
        </div>
    );
}
